<template>
  <!-- 表示順:
       1. CMS が存在する Destination / Theme タグ → 特集 (Itinerary) と同じ行表示・特集ページへ遷移
       2. Destination × Theme ページ候補 → /{destination}/{theme} へ遷移 (JCAT-5557)
       3. CMS が存在しない Destination / Theme タグ → タグ行表示 (location_on / hashtag)
       4. 体験プラン
       5. 特集 (Itineraries) -->
  <div class="flex flex-col p-4">
    <template v-if="cmsTagEntries.length > 0">
      <ul class="flex flex-col">
        <li v-for="entry in cmsTagEntries" :key="entry.hit.objectID">
          <FeaturedTagItem :featured="entry.featured" :on-click="entry.onClick" />
        </li>
      </ul>
    </template>

    <template v-if="destinationThemeEntries.length > 0">
      <ul class="flex flex-col">
        <li v-for="entry in destinationThemeEntries" :key="entry.hit.objectID">
          <DestinationThemeItem :destination-name-by-slug="destinationNameBySlug" :hit="entry.hit" :on-click="entry.onClick" />
        </li>
      </ul>
    </template>

    <template v-if="nonCmsTagEntries.length > 0">
      <ul class="flex flex-col">
        <li v-for="entry in nonCmsTagEntries" :key="entry.hit.objectID">
          <TagItem :hit="entry.hit" :on-click="entry.onClick" />
        </li>
      </ul>
    </template>

    <div v-if="hasTagsDivider" class="h-4 w-full border-b-[0.5px] border-dashed border-beige3" />

    <template v-if="experienceEntries.length > 0">
      <ul class="flex flex-col">
        <li v-for="entry in visibleExperienceEntries" :key="entry.hit.objectID">
          <ExperienceItem :converted-price="priceBySlug[entry.hit.experience.slug]" :hit="entry.hit" :on-click="entry.onClick" />
        </li>
      </ul>
      <ViewMoreRow v-if="canShowMoreExperiences" :on-click="showMoreExperiences" />
    </template>

    <div v-if="hasExperienceDivider" class="h-4 w-full border-b-[0.5px] border-dashed border-beige3" />

    <template v-if="itineraryFeatureEntries.length > 0">
      <ul class="flex flex-col">
        <li v-for="entry in visibleItineraryFeatureEntries" :key="entry.hit.objectID">
          <FeatureItem :hit="entry.hit" :on-click="entry.onClick" />
        </li>
      </ul>
      <ViewMoreRow v-if="canShowMoreItineraryFeatures" :on-click="showMoreItineraryFeatures" />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';
import type { MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import ViewMoreRow from '@/wabunka-src/features/Search/components/general/ViewMoreRow.vue';
import DestinationThemeItem from '@/wabunka-src/features/Search/components/model/DestinationThemeItem.vue';
import ExperienceItem from '@/wabunka-src/features/Search/components/model/ExperienceItem.vue';
import FeatureItem from '@/wabunka-src/features/Search/components/model/FeatureItem.vue';
import FeaturedTagItem from '@/wabunka-src/features/Search/components/model/FeaturedTagItem.vue';
import TagItem from '@/wabunka-src/features/Search/components/model/TagItem.vue';
import { useConvertedExperiencePricesQuery } from '@/wabunka-src/features/Search/composables/query/useConvertedExperiencePricesQuery';
import type { AlgoliaExperienceHit, AlgoliaFeatureHit, AlgoliaHit, AlgoliaTagHit } from '@/wabunka-src/features/Search/models/algoliaHit';
import { buildDestinationNameBySlug, isDestinationThemeFeatureHit, isExperienceHit, isFeatureHit, isTagHit } from '@/wabunka-src/features/Search/models/algoliaHit';

interface Props {
  hits: AlgoliaHit[];
  onHitClick: (hit: AlgoliaHit) => void;
  // CMS 上に存在する Destination / Theme 特集ページ。タグの「CMS存在判定」と特集カード表示に使う
  cmsDestinations: MinimumFeaturedViewModel[];
  cmsThemes: MinimumFeaturedViewModel[];
  // 全タグ（tags API）。Destination × Theme 候補のチップに実タグ名を出すための slug 逆引きに使う
  tags: WabunkaTagViewModel[];
}

const props = defineProps<Props>();

const INITIAL_EXPERIENCE = 5;
const INITIAL_ITINERARY_FEATURE = 4;
const STEP_EXPERIENCE = 3;
const STEP_FEATURE = 3;
// Destination × Theme 候補は最大 3 件のハードキャップ（View More なし。JCAT-5557 D-005）
const MAX_DESTINATION_THEME = 3;

// CMS タグ名 → 特集ビューモデルの索引。tag.name と featured.titleEn の一致で CMS 存在を判定する
const cmsDestinationByName = computed(() => new Map(props.cmsDestinations.map((f) => [f.titleEn.toLowerCase(), f])));
const cmsThemeByName = computed(() => new Map(props.cmsThemes.map((f) => [f.titleEn.toLowerCase(), f])));

const tagHits = computed<AlgoliaTagHit[]>(() => props.hits.filter(isTagHit));
// destination タグと theme(=theme/category) タグのみを対象とし、preference 等は表示しない
const destinationTagHits = computed(() => tagHits.value.filter((hit) => hit.tag.tagGroupType === 'destination'));
const themeTagHits = computed(() => tagHits.value.filter((hit) => hit.tag.tagGroupType === 'theme' || hit.tag.tagGroupType === 'category'));

// CMS 存在タグ: 特集ビューモデルを引き当てて特集カードで表示する（destination → theme の順）
const cmsTagEntries = computed(() => {
  const fromDestinations = destinationTagHits.value
    .map((hit) => ({ hit, featured: cmsDestinationByName.value.get(hit.tag.name.toLowerCase()) }))
    .filter((e): e is { hit: AlgoliaTagHit; featured: MinimumFeaturedViewModel } => e.featured !== undefined);
  const fromThemes = themeTagHits.value
    .map((hit) => ({ hit, featured: cmsThemeByName.value.get(hit.tag.name.toLowerCase()) }))
    .filter((e): e is { hit: AlgoliaTagHit; featured: MinimumFeaturedViewModel } => e.featured !== undefined);
  return [...fromDestinations, ...fromThemes].map(({ hit, featured }) => ({ hit, featured, onClick: () => props.onHitClick(hit) }));
});

// CMS 非存在タグ: 旧デザインのタグ行で表示する（destination → theme の順）
const nonCmsTagEntries = computed(() => {
  const destinations = destinationTagHits.value.filter((hit) => !cmsDestinationByName.value.has(hit.tag.name.toLowerCase()));
  const themes = themeTagHits.value.filter((hit) => !cmsThemeByName.value.has(hit.tag.name.toLowerCase()));
  return [...destinations, ...themes].map((hit) => ({ hit, onClick: () => props.onHitClick(hit) }));
});

// Destination × Theme 候補（CMSタグ行の直後・体験の前に配置）。feature(categoryLabel=destination-theme) を関連度順のまま先頭 3 件のみ表示する
const destinationThemeHits = computed<AlgoliaFeatureHit[]>(() => props.hits.filter(isDestinationThemeFeatureHit));
const destinationThemeEntries = computed(() => destinationThemeHits.value.slice(0, MAX_DESTINATION_THEME).map((hit) => ({ hit, onClick: () => props.onHitClick(hit) })));
// チップに出す destination 名は Algolia の hit ではなく tags API の全タグから引く。
// 検索語に一致した tag hit だけでは、destination 語を含まないクエリ（例: matcha）のときに名前を引けないため
const destinationNameBySlug = computed(() => buildDestinationNameBySlug(props.tags));

const experienceHits = computed<AlgoliaExperienceHit[]>(() => props.hits.filter(isExperienceHit));

// Itinerary 以外の feature (destination / theme) は tag 側で代替表示するため除外。area / category も非表示
const ITINERARY_FEATURE_CATEGORY_LABEL = 'itinerary';
const itineraryFeatureHits = computed<AlgoliaFeatureHit[]>(() => props.hits.filter(isFeatureHit).filter((hit) => hit.feature.categoryLabel === ITINERARY_FEATURE_CATEGORY_LABEL));

const experienceLimit = ref(INITIAL_EXPERIENCE);
const itineraryFeatureLimit = ref(INITIAL_ITINERARY_FEATURE);

watch(
  () => props.hits,
  () => {
    experienceLimit.value = INITIAL_EXPERIENCE;
    itineraryFeatureLimit.value = INITIAL_ITINERARY_FEATURE;
  },
);

// template から関数呼び出し（v-for 内の () => handle(hit)）を排除するため、
// computed で hit に確定済みハンドラを束ねたエントリ配列を生成しておく
const experienceEntries = computed(() => experienceHits.value.map((hit) => ({ hit, onClick: () => props.onHitClick(hit) })));
const itineraryFeatureEntries = computed(() => itineraryFeatureHits.value.map((hit) => ({ hit, onClick: () => props.onHitClick(hit) })));

const visibleExperienceEntries = computed(() => experienceEntries.value.slice(0, experienceLimit.value));
const visibleItineraryFeatureEntries = computed(() => itineraryFeatureEntries.value.slice(0, itineraryFeatureLimit.value));

const canShowMoreExperiences = computed(() => experienceHits.value.length > experienceLimit.value);
const canShowMoreItineraryFeatures = computed(() => itineraryFeatureHits.value.length > itineraryFeatureLimit.value);

// タグ群には Destination × Theme 候補も含める（体験セクションとの境界 hr 判定用）
const hasTags = computed(() => cmsTagEntries.value.length > 0 || destinationThemeEntries.value.length > 0 || nonCmsTagEntries.value.length > 0);
// hr は「タグ群 ↔ 体験」「体験 ↔ 特集」の境界のみ
const hasTagsDivider = computed(() => hasTags.value && (experienceHits.value.length > 0 || itineraryFeatureHits.value.length > 0));
const hasExperienceDivider = computed(() => experienceHits.value.length > 0 && itineraryFeatureHits.value.length > 0);

const showMoreExperiences = () => {
  experienceLimit.value += STEP_EXPERIENCE;
};
const showMoreItineraryFeatures = () => {
  itineraryFeatureLimit.value += STEP_FEATURE;
};

// 表示中の体験 slug を集約し、selectedCurrency が JPY 以外なら公開 API から換算後価格を遅延取得する。
// Algolia には JPY のみ載せ、レート換算はバックエンドに任せる方針（既存の displayPrice と整合）。
const visibleExperienceSlugs = computed(() => visibleExperienceEntries.value.map((e) => e.hit.experience.slug));
const { priceBySlug } = useConvertedExperiencePricesQuery(visibleExperienceSlugs);
</script>
